import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Grid } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { useRecoilValue } from 'recoil';
import { Report } from '../../types';
import ReportTreeView from '../ReportTreeView';
import { ResizablePanelHorizontal } from '../ResizablePanel';
import * as atoms from '../../atoms/atoms';

const INITIAL_LEFT_PANEL_WIDTH = 330;
const LEFT_PANEL_MIN_WIDTH = 230;

const LeftPaneGridItem = styled(Grid)<{
  width: number;
  $isActive: boolean;
}>`
  position: relative;
  height: 100%;
  width: ${({ width }) => width}px;
  z-index: ${({ theme }) => theme.zIndex.drawer + 1};
  box-shadow: ${({ theme, $isActive }) =>
    `${$isActive ? 2 : 1}px 0 ${theme.palette.common.gray16}`};
  transition: box-shadow 0.2s
    ${({ $isActive }) => (!$isActive ? ',width 150ms' : '')};
`;

const RightPaneGridItem = styled(Grid)`
  height: 100%;
  overflow: auto;
`;

const Wrapper = styled(Grid)`
  height: 100%;
  position: relative;
`;

type Props = {
  renderEmptyStatePage: () => JSX.Element;
  renderErrorPage: ({ error }: { error: Error }) => JSX.Element;
  reportData?: Report;
  isReportLoading?: boolean;
  useApi?: boolean;
};

const TreeViewPageShell = ({
  reportData,
  renderEmptyStatePage,
  renderErrorPage,
  isReportLoading,
  useApi = false,
}: Props) => {
  const { pathname } = useLocation();

  const [leftPanelWidth, setLeftPanelWidth] = useState(
    INITIAL_LEFT_PANEL_WIDTH,
  );
  const [isPanelDragging, setIsPanelDragging] = useState(false);
  const [isPanelGripHover, setIsPanelGripHover] = useState(false);
  const treeViewCollapsed = useRecoilValue(atoms.treeViewCollapsed);

  const isReportPageDataEmpty = useMemo(() => {
    if (!reportData) return false;

    if (pathname.includes('/model-runs')) {
      return (reportData.model_runs || []).length === 0;
    }

    if (
      !useApi &&
      (pathname.includes('/test-results') ||
        pathname.includes('/test-runs') ||
        pathname.includes('/dashboard'))
    ) {
      return Object.keys(reportData.test_results || {}).length === 0;
    }

    return false;
  }, [reportData, pathname, useApi]);

  useEffect(() => {
    if (!treeViewCollapsed.isCollapsed) {
      setLeftPanelWidth(INITIAL_LEFT_PANEL_WIDTH);
    }
  }, [treeViewCollapsed]);

  return (
    <Wrapper container wrap="nowrap">
      <LeftPaneGridItem
        item
        width={treeViewCollapsed.isCollapsed ? 0 : leftPanelWidth}
        $isActive={isPanelGripHover || isPanelDragging}
      >
        <ReportTreeView reportData={reportData} />
      </LeftPaneGridItem>

      <ResizablePanelHorizontal
        width={leftPanelWidth}
        minWidth={LEFT_PANEL_MIN_WIDTH}
        maxWidth={600}
        onDrag={setLeftPanelWidth}
        onDragStart={() => setIsPanelDragging(true)}
        onDragEnd={() => setIsPanelDragging(false)}
        onMouseEnter={() => setIsPanelGripHover(true)}
        onMouseLeave={() => setIsPanelGripHover(false)}
      />

      <RightPaneGridItem item xs overflow="hidden">
        {isReportPageDataEmpty && !isReportLoading ? (
          renderEmptyStatePage()
        ) : (
          <ErrorBoundary
            fallbackRender={({ error }) => renderErrorPage({ error })}
          >
            <Outlet />
          </ErrorBoundary>
        )}
      </RightPaneGridItem>
    </Wrapper>
  );
};

export default TreeViewPageShell;
